import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, X } from "lucide-react";

// Fruit unlock configuration - maps level to newly unlocked fruit
export const FRUIT_UNLOCKS = {
  1: { fruit: "banana", emoji: "🍌", name: "Banana" },
  2: { fruit: "apple", emoji: "🍎", name: "Apple" },
  3: { fruit: "pineapple", emoji: "🍍", name: "Pineapple" },
  4: { fruit: "pear", emoji: "🍐", name: "Pear" },
  5: { fruit: "orange", emoji: "🍊", name: "Orange" },
  6: { fruit: "peach", emoji: "🍑", name: "Peach" },
  11: { fruit: "strawberry", emoji: "🍓", name: "Strawberry" },
  13: { fruit: "lemon", emoji: "🍋", name: "Lemon" },
  15: { fruit: "cherry", emoji: "🍒", name: "Cherry" },
  17: { fruit: "watermelon", emoji: "🍉", name: "Watermelon" },
  21: { fruit: "mango", emoji: "🥭", name: "Mango" },
  26: { fruit: "kiwi", emoji: "🥝", name: "Kiwi" },
  31: { fruit: "blueberry", emoji: "🫐", name: "Blueberry" },
};

// Get newly unlocked fruit for a level (returns null if no new fruit)
export const getUnlockedFruitForLevel = (levelId) => {
  return FRUIT_UNLOCKS[levelId] || null;
};

// Check if user has already seen this fruit unlock
const SEEN_UNLOCKS_KEY = "fruitCrush_seenFruitUnlocks";

export const hasSeenFruitUnlock = (levelId) => {
  const seen = localStorage.getItem(SEEN_UNLOCKS_KEY);
  if (!seen) return false;
  const seenList = JSON.parse(seen);
  return seenList.includes(levelId);
};

export const markFruitUnlockAsSeen = (levelId) => {
  const seen = localStorage.getItem(SEEN_UNLOCKS_KEY);
  const seenList = seen ? JSON.parse(seen) : [];
  if (!seenList.includes(levelId)) {
    seenList.push(levelId);
    localStorage.setItem(SEEN_UNLOCKS_KEY, JSON.stringify(seenList));
  }
};

// New Fruit Unlocked Modal Component
export function NewFruitUnlockedModal({ isOpen, fruit, onClose }) {
  const [showSparkles, setShowSparkles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger sparkle animation after modal opens
      setTimeout(() => setShowSparkles(true), 300);
    } else {
      setShowSparkles(false);
    }
  }, [isOpen]);

  if (!isOpen || !fruit) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-fruit-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Sparkle particles */}
      {showSparkles && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute animate-float-up"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${60 + Math.random() * 40}%`,
                animationDelay: `${Math.random() * 1}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <Sparkles 
                className="text-yellow-400" 
                style={{ 
                  width: `${12 + Math.random() * 16}px`,
                  height: `${12 + Math.random() * 16}px`,
                  opacity: 0.6 + Math.random() * 0.4,
                }} 
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Modal Content */}
      <Card 
        className="relative w-full max-w-sm overflow-hidden bg-gradient-to-b from-purple-500 via-pink-500 to-orange-400 border-4 border-yellow-300 shadow-2xl animate-bounce-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-fruit-modal"
      >
        {/* Close button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/30 hover:bg-white/50 transition-colors text-white z-10"
          data-testid="new-fruit-close-btn"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Glow effect behind fruit */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-yellow-300/50 rounded-full blur-3xl animate-pulse" />
        
        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Header */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-yellow-300 animate-spin-slow" />
            <h2 className="font-display text-xl font-bold text-white drop-shadow-lg">
              NEW FRUIT!
            </h2>
            <Sparkles className="w-6 h-6 text-yellow-300 animate-spin-slow" />
          </div>
          
          {/* Fruit display */}
          <div className="relative mb-6">
            <div className="text-9xl animate-bounce-slow drop-shadow-2xl">
              {fruit.emoji}
            </div>
            {/* Shine effect */}
            <div className="absolute top-4 right-1/4 w-8 h-8 bg-white/60 rounded-full blur-md animate-ping" />
          </div>
          
          {/* Fruit name */}
          <h3 className="font-display text-3xl font-bold text-white mb-2 drop-shadow-lg">
            {fruit.name}
          </h3>
          <p className="text-white/90 text-sm mb-6">
            You've unlocked a new fruit type!
          </p>
          
          {/* Continue button */}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            size="lg"
            className="w-full font-display font-bold text-lg bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white border-2 border-yellow-300 shadow-lg"
            data-testid="new-fruit-awesome-btn"
          >
            Awesome! 🎉
          </Button>
        </div>

        {/* Bottom decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300" />
      </Card>
    </div>
  );
}

// Hook to manage new fruit notifications
export function useNewFruitNotification() {
  const [showModal, setShowModal] = useState(false);
  const [unlockedFruit, setUnlockedFruit] = useState(null);

  const checkAndShowUnlock = useCallback((completedLevelId) => {
    const fruit = getUnlockedFruitForLevel(completedLevelId);
    
    if (fruit && !hasSeenFruitUnlock(completedLevelId)) {
      setUnlockedFruit(fruit);
      setShowModal(true);
      markFruitUnlockAsSeen(completedLevelId);
      return true;
    }
    return false;
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setTimeout(() => setUnlockedFruit(null), 300);
  }, []);

  return {
    showModal,
    unlockedFruit,
    checkAndShowUnlock,
    closeModal,
  };
}
